/**
 * ProtectedRoute - single reusable, role-aware route guard.
 *
 * Props:
 *   requireAuth   (bool, default true) - user must be authenticated.
 *   requiredRole  (string, optional)   - 'admin' requires an admin/super_admin.
 *   children                            - the protected element (or <Outlet/> zone).
 *
 * Behaviour:
 *   - Still hydrating auth        -> show a loader (avoids a false redirect flash).
 *   - Not authenticated           -> redirect to /login, preserving return-to.
 *   - Authenticated, wrong role   -> redirect to the app home (/user/dashboard).
 *   - Otherwise                   -> render children.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const ProtectedRoute = ({ requireAuth = true, requiredRole = null, children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (requireAuth && !isAuthenticated) {
    // Preserve where the user was headed so login can return them, and explain why.
    return (
      <Navigate
        to="/login"
        replace
        state={{
          returnTo: location.pathname + location.search,
          message: 'Please sign in to continue — we will take you right back.',
        }}
      />
    );
  }

  if (requiredRole === 'admin' && !isAdmin) {
    // Authenticated but not permitted: send to the app home rather than crash.
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
